import { renderLucideIcon } from '../utils/lucide-icons.js'

var DEFAULT_LABELS = {
  title: 'Signature email',
  toolbar: 'Outils de mise en forme',
  html: 'HTML',
  addLink: 'Ajouter un lien',
  removeLink: 'Supprimer le lien',
  image: 'Ajouter une image',
  placeholder: 'Saisissez votre signature...',
  htmlHelp: 'Le HTML est nettoyé automatiquement avant sauvegarde.'
}

var TOOLBAR = [
  { command: 'bold', icon: 'bold', label: 'Gras' },
  { command: 'italic', icon: 'italic', label: 'Italique' },
  { command: 'insertUnorderedList', icon: 'list', label: 'Liste' },
  { command: 'justifyLeft', icon: 'alignLeft', label: 'Aligner à gauche' },
  { command: 'justifyCenter', icon: 'alignCenter', label: 'Centrer' },
  { command: 'justifyRight', icon: 'alignRight', label: 'Aligner à droite' },
  { command: 'createLink', icon: 'link', label: 'Lien' },
  { command: 'unlink', icon: 'link2Off', label: 'Supprimer le lien' },
  { command: 'removeFormat', icon: 'removeFormatting', label: 'Nettoyer' }
]

function createEmailSignatureEditor(options) {
  options = options || {}
  var labels = Object.assign({}, DEFAULT_LABELS, options.labels || {})
  var initialHtml = sanitizeSignatureHtml(options.html || '')
  var state = {
    html: initialHtml,
    mode: 'visual',
    onChange: typeof options.onChange === 'function' ? options.onChange : null
  }
  var root = options.target || null
  var showHeader = options.showHeader !== false

  function mount(target) {
    root = target || root
    if (!root) throw new Error('EmailSignatureEditor target is required')
    root.innerHTML = renderEditor(labels, state.html, showHeader)
    bind(root, state, labels)
    syncAll(root, state.html)
    return api
  }

  function getHtml() {
    if (root) {
      syncStateFromCurrentEditor(root, state)
    }
    return sanitizeSignatureHtml(state.html)
  }

  function setHtml(nextHtml) {
    state.html = sanitizeSignatureHtml(nextHtml || '')
    state.mode = 'visual'
    if (root) syncAll(root, state.html)
  }

  function destroy() {
    if (root) root.innerHTML = ''
  }

  var api = { mount, getHtml, setHtml, destroy }
  if (root) mount(root)
  return api
}

function renderEditor(labels, html, showHeader) {
  return (
    '<div class="wc-email-signature-editor-ui">' +
      (showHeader ? '<div class="wc-email-signature-editor-head">' +
        '<div>' +
          '<h2>' + escapeHtml(labels.title) + '</h2>' +
          '<p>' + escapeHtml(labels.htmlHelp) + '</p>' +
        '</div>' +
      '</div>' : '') +
      '<div class="wc-email-signature-toolbar" role="toolbar" aria-label="' + escapeAttributeValue(labels.toolbar) + '">' +
        TOOLBAR.map(function (tool) {
          return '<button type="button" data-signature-command="' + escapeAttributeValue(tool.command) + '" title="' + escapeAttributeValue(tool.label) + '" aria-label="' + escapeAttributeValue(tool.label) + '">' + renderLucideIcon(tool.icon, { size: 16 }) + '</button>'
        }).join('') +
        '<button type="button" data-signature-image title="' + escapeAttributeValue(labels.image) + '" aria-label="' + escapeAttributeValue(labels.image) + '">' + renderLucideIcon('image', { size: 16 }) + '</button>' +
        '<button type="button" data-signature-toggle-html title="' + escapeAttributeValue(labels.html) + '" aria-label="' + escapeAttributeValue(labels.html) + '">' + renderLucideIcon('code', { size: 16 }) + '</button>' +
        '<input type="file" accept="image/*" data-signature-file hidden>' +
      '</div>' +
      '<div class="wc-email-signature-editor-window" data-signature-window>' +
        '<div class="wc-email-signature-editable" data-signature-editable contenteditable="true" role="textbox" aria-multiline="true" data-placeholder="' + escapeAttributeValue(labels.placeholder) + '">' + html + '</div>' +
        '<textarea class="wc-email-signature-html-input" data-signature-html spellcheck="false">' + escapeHtml(html) + '</textarea>' +
      '</div>' +
    '</div>'
  )
}

function bind(root, state, labels) {
  var editable = root.querySelector('[data-signature-editable]')
  var htmlInput = root.querySelector('[data-signature-html]')
  var fileInput = root.querySelector('[data-signature-file]')

  root.querySelectorAll('[data-signature-command]').forEach(function (button) {
    button.addEventListener('click', function () {
      ensureVisualMode(root, state)
      runCommand(root, button.getAttribute('data-signature-command'), labels)
      updateFromEditable(root, state)
    })
  })

  root.querySelector('[data-signature-image]')?.addEventListener('click', function () {
    ensureVisualMode(root, state)
    fileInput?.click()
  })

  root.querySelector('[data-signature-toggle-html]')?.addEventListener('click', function () {
    toggleHtmlMode(root, state)
  })

  fileInput?.addEventListener('change', function () {
    readImageFiles(fileInput.files, function (dataUrl) {
      insertHtml(editable, '<img src="' + escapeAttributeValue(dataUrl) + '" alt="">')
      updateFromEditable(root, state)
    })
    fileInput.value = ''
  })

  editable?.addEventListener('input', function () {
    updateFromEditable(root, state)
  })

  editable?.addEventListener('paste', function () {
    setTimeout(function () {
      updateFromEditable(root, state)
      syncAll(root, state.html)
    }, 0)
  })

  htmlInput?.addEventListener('input', function () {
    state.html = sanitizeSignatureHtml(htmlInput.value || '')
    syncEditable(root, state.html)
    notifyChange(state)
  })

}

function runCommand(root, command, labels) {
  var editable = root.querySelector('[data-signature-editable]')
  editable?.focus()
  if (command === 'createLink') {
    var url = window.prompt(labels.addLink, 'https://')
    if (!url || !isAllowedUrl(url)) return
    document.execCommand('createLink', false, url)
    return
  }
  document.execCommand(command, false, null)
}

function updateFromEditable(root, state) {
  var editable = root.querySelector('[data-signature-editable]')
  state.html = sanitizeSignatureHtml(editable?.innerHTML || '')
  syncHtml(root, state.html)
  notifyChange(state)
}

function updateFromHtml(root, state) {
  var htmlInput = root.querySelector('[data-signature-html]')
  state.html = sanitizeSignatureHtml(htmlInput?.value || '')
  syncEditable(root, state.html)
  syncHtml(root, state.html)
  notifyChange(state)
}

function syncStateFromCurrentEditor(root, state) {
  if (state.mode === 'html') {
    var htmlInput = root.querySelector('[data-signature-html]')
    state.html = sanitizeSignatureHtml(htmlInput?.value || '')
    syncEditable(root, state.html)
    syncHtml(root, state.html)
    return
  }

  var editable = root.querySelector('[data-signature-editable]')
  state.html = sanitizeSignatureHtml(editable?.innerHTML || '')
  syncHtml(root, state.html)
}

function syncAll(root, html) {
  syncEditable(root, html)
  syncHtml(root, html)
  syncMode(root, 'visual')
}

function syncEditable(root, html) {
  var editable = root.querySelector('[data-signature-editable]')
  if (editable && editable.innerHTML !== html) editable.innerHTML = html
}

function syncHtml(root, html) {
  var htmlInput = root.querySelector('[data-signature-html]')
  if (htmlInput && htmlInput.value !== html) htmlInput.value = html
}

function toggleHtmlMode(root, state) {
  if (state.mode === 'html') {
    updateFromHtml(root, state)
    state.mode = 'visual'
  } else {
    updateFromEditable(root, state)
    state.mode = 'html'
  }
  syncMode(root, state.mode)
}

function ensureVisualMode(root, state) {
  if (state.mode !== 'html') return
  updateFromHtml(root, state)
  state.mode = 'visual'
  syncMode(root, state.mode)
}

function syncMode(root, mode) {
  var windowEl = root.querySelector('[data-signature-window]')
  var toggle = root.querySelector('[data-signature-toggle-html]')
  if (windowEl) windowEl.classList.toggle('is-html-mode', mode === 'html')
  if (toggle) toggle.classList.toggle('is-active', mode === 'html')
}

function notifyChange(state) {
  if (state.onChange) state.onChange(state.html)
}

function insertHtml(editable, html) {
  if (!editable) return
  editable.focus()
  document.execCommand('insertHTML', false, html)
}

function readImageFiles(files, onImage) {
  Array.prototype.slice.call(files || []).forEach(function (file) {
    if (!file || !/^image\//.test(file.type || '')) return
    if (file.size > 1024 * 512) {
      window.alert('Image trop lourde pour une signature email.')
      return
    }
    var reader = new FileReader()
    reader.onload = function () {
      if (typeof reader.result === 'string' && isAllowedUrl(reader.result)) onImage(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

function sanitizeSignatureHtml(value) {
  var template = document.createElement('template')
  template.innerHTML = String(value || '')
  var blockedTags = ['script', 'iframe', 'object', 'embed', 'style', 'meta', 'link']
  blockedTags.forEach(function (tagName) {
    template.content.querySelectorAll(tagName).forEach(function (node) {
      node.parentNode && node.parentNode.removeChild(node)
    })
  })
  template.content.querySelectorAll('*').forEach(function (node) {
    Array.prototype.slice.call(node.attributes || []).forEach(function (attribute) {
      var name = attribute.name.toLowerCase()
      var attributeValue = String(attribute.value || '').trim()
      if (/^on/.test(name)) {
        node.removeAttribute(attribute.name)
      } else if ((name === 'href' || name === 'src') && !isAllowedUrl(attributeValue)) {
        node.removeAttribute(attribute.name)
      }
    })
  })
  return template.innerHTML.trim()
}

function isAllowedUrl(value) {
  return /^(https?:|mailto:)/i.test(value) || /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=\s]+$/i.test(value)
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttributeValue(value) {
  return escapeHtml(value).replace(/`/g, '&#96;')
}

export { createEmailSignatureEditor, sanitizeSignatureHtml }
